/*
 * AppPreviewToolbar.tsx  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Key } from "ts-key-enum";

import { BEM, useToggle, useTranslate } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";

import { ToolbarButton, ToolbarButtonProps } from "../../../../ui/components/Toolbar/ToolbarButton";
import { AppPreviewHistory, IAppPreviewHistoryState } from "../AppPreviewHistory";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import {
	getPreviewResolutionById,
	previewResolutionsOptions,
	TPreviewResolutionId,
	usePreviewResolutionContext
} from "../../../../previewResolution";
import { DROPDOWN_STYLE } from "../../../../ui/components/DropdownMenu";
import { FC_ANCHOR } from "../../../../ui/components/FloatingContainer";
import { PreviewResolutionSettings } from "../../../../ui/components/PreviewResolutionSettings";

export interface IAppPreviewToolbarProps {
	className?: string;
	closePreview?: () => void;
	history: AppPreviewHistory;
	previewSize?: string;
	onPreviewSizeChange?: (size: string) => void;
	onPathSubmit?: (path: string) => void;
}

const PREVIEW_ID_BTN_STYLE: React.CSSProperties = { width: "130px" };
const PREVIEW_WIDTH_BTN_STYLE: React.CSSProperties = { width: "55px" };

export const AppPreviewToolbar: React.FC<IAppPreviewToolbarProps> = ({
	className,
	closePreview,
	history,
	onPathSubmit
}) => {
	const previewResolutionCtx = usePreviewResolutionContext();

	const baseClass = BEM("edt-app-preview", "toolbar");

	const t = useTranslate();
	const previewBtnRef = useRef();
	const [ historyState, setHistoryState ] = useState<IAppPreviewHistoryState>(() => history.getState());
	const [ pathValue, setPathValue ] = useState(historyState.current);
	const [ previewSettingsOpen, togglePreviewSettings, , closePreviewSettings ] = useToggle(false);

	const selectedResolution = getPreviewResolutionById(previewResolutionCtx?.previewResolutionId);

	const goBack = useCallback(() => {
		history.goBack();
	}, []);

	const goForward = useCallback(() => {
		history.goForward();
	}, []);

	const changePathValue = useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
		const value = event.currentTarget.value;
		setPathValue(value);
	}, []);

	const submitPath = useCallback((path: string) => {
		if (isFunction(onPathSubmit)) {
			onPathSubmit(path);
		}
	}, []);

	const handleInputKeyDown = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
		(event) => {
			if (event.key === Key.Enter) {
				if (isFunction(onPathSubmit)) {
					submitPath(event.currentTarget.value);
				}
			}
		},
		[ submitPath ]
	);

	const selectPreviewResolution = useCallback<ToolbarButtonProps["onMenuItemClick"]>(
		(resolution) => {
			previewResolutionCtx?.setPreviewResolutionId(resolution.id as TPreviewResolutionId);
		},
		[ previewResolutionCtx?.setPreviewResolutionId ]
	);

	useSimpleEvent(history.onChange, () => {
		setHistoryState(history.getState());
	});

	useEffect(() => {
		setPathValue(historyState.current);
	}, [ historyState.current ]);

	if (className) {
		baseClass.add(className);
	}

	return (
		<>
			<ToolbarButton
				icon="mdi/arrow-left"
				onClick={goBack}
				tooltip="Go back"
				disabled={!historyState.previous}
			/>
			<ToolbarButton
				icon="mdi/arrow-right"
				onClick={goForward}
				tooltip="Go forward"
				disabled={!historyState.next}
			/>

			<input
				type="text"
				className={baseClass.element("url").toClassName()}
				value={pathValue}
				onChange={changePathValue}
				onKeyDown={handleInputKeyDown}
			/>

			<ToolbarButton
				icon={selectedResolution.icon}
				label={t("editor", selectedResolution.label)}
				menuItems={previewResolutionsOptions}
				selectedMenuItemId={previewResolutionCtx?.previewResolutionId}
				menuStyle={DROPDOWN_STYLE.COMPACT}
				menuAnchor={FC_ANCHOR.LEFT_BOTTOM}
				onMenuItemClick={selectPreviewResolution}
				cssStyle={PREVIEW_ID_BTN_STYLE}
			/>

			<ToolbarButton
				ref={previewBtnRef}
				onClick={togglePreviewSettings}
				cssStyle={PREVIEW_WIDTH_BTN_STYLE}
			>
				<span>{previewResolutionCtx.previewWidth}px</span>
			</ToolbarButton>
			<PreviewResolutionSettings
				className={baseClass.element("resolution-settings").toClassName()}
				open={previewSettingsOpen}
				target={previewBtnRef}
				onClose={closePreviewSettings}
			/>

			<ToolbarButton
				icon="mdi/close"
				onClick={closePreview}
				tooltip="Close preview"
				mark="app-preview-close"
			/>
		</>
	);
};
